<template>
  <v-stepper-content step="2">
    <v-card flat class="mb-4">
      <v-card-text>
        <!-- Vista inicial simple cuando no hay datos ingresados -->
        <div v-if="!initialKm && !driverType && !vehicleCondition" class="text-center py-5">
          <h3 class="headline mb-5">Información del vehículo</h3>
          <v-row justify="center">
            <v-col cols="12" md="8">
              <div class="subtitle-1 font-weight-bold mb-3">Tipo de vehículo</div>
              <v-card outlined class="pa-4 mb-4">
                <v-radio-group 
                  v-model="localDriverType" 
                  required 
                  :rules="validations.registered"
                  :error-messages="errors.registered" 
                  row 
                  class="justify-center"
                  @change="$emit('update:driverType', $event)"
                >
                  <v-radio 
                    v-for="option in vehicleTypeOptions" 
                    :key="option.value" 
                    :label="option.text"
                    :value="option.value" 
                    :color="option.color"
                    :disabled="option.disabled"
                  ></v-radio>
                </v-radio-group>
              </v-card>
            </v-col>
          </v-row>
        </div>
        
        <!-- Vista completa cuando ya hay datos ingresados -->
        <div v-else>
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4 position-relative vehicle-info-card">
                <div class="subtitle-1 font-weight-bold mb-4">
                  <v-icon left color="primary">mdi-tag-outline</v-icon>
                  Tipo de vehículo
                </div>
                <v-radio-group 
                  v-model="localDriverType" 
                  required
                  :rules="validations.registered" 
                  :error-messages="errors.registered" 
                  row 
                  class="ml-2 mb-4"
                  @change="$emit('update:driverType', $event)"
                >
                  <v-radio 
                    v-for="option in vehicleTypeOptions" 
                    :key="option.value" 
                    :label="option.text"
                    :value="option.value" 
                    :color="option.color"
                    :disabled="option.disabled"
                  ></v-radio>
                </v-radio-group>
                <div class="subtitle-1 font-weight-bold mb-2">
                  <v-icon left color="primary">mdi-speedometer</v-icon>
                  Kilómetros iniciales
                </div>
                <v-text-field 
                  v-model="localInitialKm" 
                  label="Km's Iniciales" 
                  outlined 
                  required 
                  dense
                  prepend-inner-icon="mdi-speedometer" 
                  :rules="validations.initial_km"
                  :error-messages="errors.initial_km"
                  :error="!localInitialKm || localInitialKm === ''"
                  class="mb-2"
                  @input="$emit('update:initialKm', $event)"
                ></v-text-field>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4 vehicle-condition-card">
                <div class="subtitle-1 font-weight-bold mb-4">
                  <v-icon left color="primary">mdi-car-info</v-icon>
                  Estado del vehículo
                </div>
                <v-radio-group 
                  v-model="localVehicleCondition" 
                  required
                  :rules="validations.vehicle_status" 
                  :error-messages="errors.vehicle_status"
                  :error="!localVehicleCondition || localVehicleCondition === ''"
                  class="vehicle-condition-group"
                  @change="$emit('update:vehicleCondition', $event)"
                >
                  <v-row>
                    <v-col cols="6">
                      <v-hover v-slot="{ hover }">
                        <v-card 
                          :elevation="hover || vehicleCondition === 'good' ? 6 : 1"
                          class="pa-4 text-center transition-swing"
                          :class="{
                            'success lighten-5': vehicleCondition === 'good',
                            'error-border': !localVehicleCondition || localVehicleCondition === ''
                          }"
                          @click="localVehicleCondition = 'good'; $emit('update:vehicleCondition', 'good')"
                        >
                          <v-icon size="48" color="success">mdi-check-circle</v-icon>
                          <div class="subtitle-1 mt-3 font-weight-medium">BUENO</div>
                          <v-radio value="good" class="vehicle-condition-radio" hide-details></v-radio>
                        </v-card>
                      </v-hover>
                    </v-col>
                    <v-col cols="6">
                      <v-hover v-slot="{ hover }">
                        <v-card 
                          :elevation="hover || vehicleCondition === 'damages' ? 6 : 1"
                          class="pa-4 text-center transition-swing"
                          :class="{
                            'error lighten-5': vehicleCondition === 'damages',
                            'error-border': !localVehicleCondition || localVehicleCondition === ''
                          }"
                          @click="localVehicleCondition = 'damages'; $emit('update:vehicleCondition', 'damages')"
                        >
                          <v-icon size="48" color="error">mdi-alert-circle</v-icon>
                          <div class="subtitle-1 mt-3 font-weight-medium">HAY DAÑOS</div>
                          <v-radio value="damages" class="vehicle-condition-radio" hide-details></v-radio>
                        </v-card>
                      </v-hover>
                    </v-col>
                  </v-row>
                </v-radio-group>

                <!-- Checkbox para mostrar información adicional -->
                <v-checkbox
                  v-model="localShowAdditionalInfo"
                  label="Agregar información adicional"
                  color="primary"
                  class="mt-4"
                  @change="$emit('update:showAdditionalInfo', $event)"
                ></v-checkbox>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Usar el ImageManager para manejar las imágenes y documentos de daños -->
        <ImageManager
          :vehicle-condition="localVehicleCondition"
          :damage-notes="damageNotes"
          :saved-images="savedImages"
          :new-damage-images="newDamageImages"
          :new-image-previews="newImagePreviews"
          :images-to-remove="imagesToRemove"
          @update:damageNotes="$emit('update:damageNotes', $event)"
          @update:newDamageImages="$emit('update:newDamageImages', $event)"
          @update:newImagePreviews="$emit('update:newImagePreviews', $event)"
          @update:imagesToRemove="$emit('update:imagesToRemove', $event)"
          @images-changed="$emit('handle-image-change', $event)"
          @image-removed="$emit('image-removed', $event)"
        />

        <!-- Información adicional -->
        <v-slide-y-transition>
          <v-card v-if="localShowAdditionalInfo" class="mb-4 damage-details-card" outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="primary">mdi-information-outline</v-icon>
              Información adicional
            </v-card-title>
            <v-card-text>
              <v-textarea
                v-model="localAdditionalInfo"
                label="Información adicional"
                placeholder="Agrega información adicional sobre el vehículo..."
                outlined
                dense
                rows="3"
                counter="500"
                maxlength="500"
                :rules="validations.additional_info"
                :error-messages="errors.additional_info"
                @input="$emit('update:additionalInfoReception', $event)"
              ></v-textarea>

              <!-- Sección de imágenes guardadas -->
              <div v-if="savedAdditionalImages && savedAdditionalImages.length > 0" class="mb-4 saved-images-container">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="primary">mdi-image-multiple</v-icon>
                  Imágenes guardadas ({{ savedAdditionalImages.length - additionalImagesToRemove.length }})
                </div>
                <v-row>
                  <v-col
                    v-for="(image, index) in savedAdditionalImages"
                    :key="`saved-${index}`"
                    cols="6"
                    sm="4"
                    md="3"
                  >
                    <v-card
                      class="image-preview-card elevation-2"
                      :class="{ 'disabled-card': additionalImagesToRemove.includes(image.id) }"
                      @click="!additionalImagesToRemove.includes(image.id) && openImageDialog(image.url)"
                    >
                      <v-img
                        :src="image.url"
                        height="120"
                        cover
                        class="image-preview"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                          </v-row>
                        </template>

                        <div class="image-overlay d-flex align-center justify-center">
                          <v-btn
                            v-if="!additionalImagesToRemove.includes(image.id)"
                            icon
                            color="white"
                            @click.stop="removeSavedAdditionalImage(index)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                          <v-btn
                            v-if="!additionalImagesToRemove.includes(image.id)"
                            icon
                            color="white"
                            @click.stop="openImageDialog(image.url)"
                          >
                            <v-icon>mdi-magnify-plus</v-icon>
                          </v-btn>
                        </div>
                      </v-img>

                      <v-chip
                        v-if="additionalImagesToRemove.includes(image.id)"
                        class="image-badge"
                        x-small
                        color="error"
                      >
                        Eliminada
                      </v-chip>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Sección de nuevas imágenes -->
              <div v-if="newAdditionalImagePreviews && newAdditionalImagePreviews.length > 0" class="mb-4 new-images-container">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="success">mdi-image-plus</v-icon>
                  Nuevas imágenes ({{ newAdditionalImagePreviews.length }})
                </div>
                <v-row>
                  <v-col
                    v-for="(preview, index) in newAdditionalImagePreviews"
                    :key="`new-${index}`"
                    cols="6"
                    sm="4"
                    md="3"
                  >
                    <v-card class="new-image-preview-card elevation-2" @click="openImageDialog(preview)">
                      <v-img
                        :src="preview"
                        height="120"
                        cover
                        class="image-preview"
                      >
                        <div class="image-overlay d-flex align-center justify-center">
                          <v-btn icon color="white" @click.stop="removeNewAdditionalImage(index)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                          <v-btn icon color="white" @click.stop="openImageDialog(preview)">
                            <v-icon>mdi-magnify-plus</v-icon>
                          </v-btn>
                        </div>
                      </v-img>

                      <v-chip class="image-badge" x-small color="success">
                        Nueva
                      </v-chip>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Input para subir nuevas imágenes -->
              <v-file-input
                v-if="canAddMoreAdditionalImages"
                v-model="tempAdditionalImages"
                accept="image/*"
                multiple
                label="Agregar imágenes adicionales"
                prepend-icon="mdi-camera"
                outlined
                dense
                chips
                show-size
                @change="handleAdditionalImageUpload"
                :hint="`Puedes agregar ${remainingAdditionalImageSlots} imagen(es) más. Máximo 4 imágenes.`"
                persistent-hint
              ></v-file-input>

              <v-alert
                v-if="!canAddMoreAdditionalImages"
                type="info"
                dense
                class="mt-2"
              >
                <v-icon left small>mdi-information</v-icon>
                Has alcanzado el límite máximo de 4 imágenes.
              </v-alert>

              <!-- Sección de documentos adicionales -->
              <v-divider class="my-4"></v-divider>
              
              <div class="subtitle-2 mb-3">
                <v-icon left color="primary">mdi-file-document-multiple-outline</v-icon>
                Documentos adicionales
              </div>

              <!-- Sección de documentos guardados -->
              <div v-if="savedAdditionalDocs && savedAdditionalDocs.length > 0" class="mb-4 saved-docs-container">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="primary">mdi-file-check</v-icon>
                  Documentos guardados ({{ savedAdditionalDocs.length - additionalDocsToRemove.length }})
                </div>
                <v-list dense>
                  <v-list-item
                    v-for="(doc, index) in savedAdditionalDocs"
                    :key="`saved-doc-${index}`"
                    :class="{ 'disabled-item': additionalDocsToRemove.includes(doc.id) }"
                  >
                    <v-list-item-avatar>
                      <v-icon :color="getFileIconColor(doc.filename || doc.name)">{{ getFileIcon(doc.filename || doc.name) }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.filename || doc.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="doc.size">{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="d-flex">
                        <v-btn
                          v-if="!additionalDocsToRemove.includes(doc.id)"
                          icon
                          small
                          @click="downloadDoc(doc.url, doc.filename || doc.name)"
                        >
                          <v-icon small>mdi-download</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="!additionalDocsToRemove.includes(doc.id)"
                          icon
                          small
                          color="error"
                          @click="removeSavedAdditionalDoc(index)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                        <v-chip
                          v-if="additionalDocsToRemove.includes(doc.id)"
                          x-small
                          color="error"
                        >
                          Eliminado
                        </v-chip>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>

              <!-- Sección de nuevos documentos -->
              <div v-if="newAdditionalDocs && newAdditionalDocs.length > 0" class="mb-4 new-docs-container">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="success">mdi-file-plus</v-icon>
                  Nuevos documentos ({{ newAdditionalDocs.length }})
                </div>
                <v-list dense>
                  <v-list-item
                    v-for="(doc, index) in newAdditionalDocs"
                    :key="`new-doc-${index}`"
                    class="new-doc-item"
                  >
                    <v-list-item-avatar>
                      <v-icon :color="getFileIconColor(doc.name)">{{ getFileIcon(doc.name) }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeNewAdditionalDoc(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                    <v-chip class="ml-2" x-small color="success">
                      Nuevo
                    </v-chip>
                  </v-list-item>
                </v-list>
              </div>

              <!-- Input para subir nuevos documentos -->
              <v-file-input
                v-if="canAddMoreAdditionalDocs"
                v-model="tempAdditionalDocs"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                multiple
                label="Agregar documentos adicionales"
                prepend-icon="mdi-paperclip"
                outlined
                dense
                chips
                show-size
                @change="handleAdditionalDocUpload"
                :hint="`Puedes agregar ${remainingAdditionalDocSlots} documento(s) más. Máximo 4 documentos. Formatos: PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, RAR`"
                persistent-hint
              ></v-file-input>

              <v-alert
                v-if="!canAddMoreAdditionalDocs"
                type="info"
                dense
                class="mt-2"
              >
                <v-icon left small>mdi-information</v-icon>
                Has alcanzado el límite máximo de 4 documentos.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-slide-y-transition>

        <!-- Diálogo para vista previa de imágenes -->
        <v-dialog v-model="additionalImageDialog" max-width="90vw" max-height="90vh">
          <v-card class="image-dialog-content">
            <v-card-title class="pa-2">
              <v-spacer></v-spacer>
              <v-btn icon @click="additionalImageDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-img
                :src="selectedAdditionalImageUrl"
                contain
                max-height="80vh"
              ></v-img>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-space-between">
      <v-btn text @click="$emit('previous-step')">
        <v-icon left>mdi-arrow-left</v-icon>
        Anterior
      </v-btn>
      <v-btn 
        color="primary" 
        @click="$emit('next-step')"
        :disabled="!canProceedToNext"
      >
        Siguiente
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
    </div>
  </v-stepper-content>
</template>

<script>
import ImageManager from './ImageManager.vue'

export default {
  name: 'VehicleConditionStep',
  components: {
    ImageManager
  },
  props: {
    initialKm: {
      type: [String, Number],
      default: null
    },
    driverType: {
      type: String,
      default: ''
    },
    vehicleCondition: {
      type: String,
      default: ''
    },
    damageNotes: {
      type: String,
      default: ''
    },
    tempDamageImages: {
      type: Array,
      default: () => []
    },
    newImagePreviews: {
      type: Array,
      default: () => []
    },
    remainingImageSlots: {
      type: Number,
      default: 4
    },
    vehicleTypeOptions: {
      type: Array,
      default: () => []
    },
    validations: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    savedImages: {
      type: Array,
      default: () => []
    },
    newDamageImages: {
      type: Array,
      default: () => []
    },
    imagesToRemove: {
      type: Array,
      default: () => []
    },
    additionalInfoReception: {
      type: String,
      default: ''
    },
    showAdditionalInfo: {
      type: Boolean,
      default: false
    },
    newAdditionalImages: {
      type: Array,
      default: () => []
    },
    newAdditionalImagePreviews: {
      type: Array,
      default: () => []
    },
    savedAdditionalImages: {
      type: Array,
      default: () => []
    },
    additionalImagesToRemove: {
      type: Array,
      default: () => []
    },
    newAdditionalDocs: {
      type: Array,
      default: () => []
    },
    savedAdditionalDocs: {
      type: Array,
      default: () => []
    },
    additionalDocsToRemove: {
      type: Array,
      default: () => []
    },
    savedDamageDocs: {
      type: Array,
      default: () => []
    },
    newDamageDocs: {
      type: Array,
      default: () => []
    },
    damageDocsToRemove: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localInitialKm: this.initialKm,
      localDriverType: this.driverType,
      localVehicleCondition: this.vehicleCondition,
      localDamageNotes: this.damageNotes,
      localAdditionalInfo: this.additionalInfoReception,
      localShowAdditionalInfo: this.showAdditionalInfo,
      tempAdditionalImages: [],
      tempAdditionalDocs: [],
      tempDamageDocs: [],
      additionalImageDialog: false,
      selectedAdditionalImageUrl: ''
    }
  },
  watch: {
    initialKm(newVal) {
      this.localInitialKm = newVal
    },
    driverType(newVal) {
      this.localDriverType = newVal
    },
    vehicleCondition(newVal) {
      this.localVehicleCondition = newVal
    },
    damageNotes(newVal) {
      this.localDamageNotes = newVal
    },
    additionalInfoReception(newVal) {
      this.localAdditionalInfo = newVal
    },
    showAdditionalInfo(newVal) {
      this.localShowAdditionalInfo = newVal
    },
    vehicleTypeOptions: {
      handler(newOptions) {
        // Si solo hay una opción disponible y no hay valor seleccionado, seleccionarla automáticamente
        if (newOptions && newOptions.length > 0 && !this.localDriverType) {
          const enabledOptions = newOptions.filter(opt => !opt.disabled)
          if (enabledOptions.length === 1) {
            this.localDriverType = enabledOptions[0].value
            this.$emit('update:driverType', enabledOptions[0].value)
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Verificar al montar si solo hay una opción disponible
    if (this.vehicleTypeOptions && this.vehicleTypeOptions.length > 0 && !this.localDriverType) {
      const enabledOptions = this.vehicleTypeOptions.filter(opt => !opt.disabled)
      if (enabledOptions.length === 1) {
        this.localDriverType = enabledOptions[0].value
        this.$emit('update:driverType', enabledOptions[0].value)
      }
    }
  },
  computed: {
    canAddMoreAdditionalImages() {
      return this.remainingAdditionalImageSlots > 0
    },
    remainingAdditionalImageSlots() {
      const savedCount = this.savedAdditionalImages ? this.savedAdditionalImages.length - this.additionalImagesToRemove.length : 0
      const newCount = this.newAdditionalImages ? this.newAdditionalImages.length : 0
      return Math.max(0, 4 - savedCount - newCount)
    },
    canAddMoreAdditionalDocs() {
      return this.remainingAdditionalDocSlots > 0
    },
    remainingAdditionalDocSlots() {
      const savedCount = this.savedAdditionalDocs ? this.savedAdditionalDocs.length - this.additionalDocsToRemove.length : 0
      const newCount = this.newAdditionalDocs ? this.newAdditionalDocs.length : 0
      return Math.max(0, 4 - savedCount - newCount)
    },
    canAddMoreDamageDocs() {
      return this.remainingDamageDocSlots > 0
    },
    remainingDamageDocSlots() {
      const savedCount = this.savedDamageDocs ? this.savedDamageDocs.length - this.damageDocsToRemove.length : 0
      const newCount = this.newDamageDocs ? this.newDamageDocs.length : 0
      return Math.max(0, 4 - savedCount - newCount)
    },
    canProceedToNext() {
      // Validar que existan los 3 campos obligatorios
      const hasInitialKm = this.localInitialKm && this.localInitialKm !== ''
      const hasDriverType = this.localDriverType && this.localDriverType !== ''
      const hasVehicleCondition = this.localVehicleCondition && this.localVehicleCondition !== ''
      
      return hasInitialKm && hasDriverType && hasVehicleCondition
    }
  },
  methods: {
    removeSavedAdditionalImage(index) {
      // Marcar para eliminar del servidor
      const image = this.savedAdditionalImages[index]
      const imageId = image && image.id !== undefined ? image.id : index
      const updatedImagesToRemove = [...this.additionalImagesToRemove, imageId]
      this.$emit('update:additionalImagesToRemove', updatedImagesToRemove)
      this.$emit('additional-image-removed', { type: 'saved', imageId })
    },
    removeNewAdditionalImage(index) {
      // Eliminar la vista previa
      const updatedPreviews = [...this.newAdditionalImagePreviews]
      updatedPreviews.splice(index, 1)
      this.$emit('update:newAdditionalImagePreviews', updatedPreviews)

      // Eliminar el archivo
      const updatedFiles = [...this.newAdditionalImages]
      updatedFiles.splice(index, 1)
      this.$emit('update:newAdditionalImages', updatedFiles)
      this.$emit('additional-image-removed', { type: 'new', index })
    },
    handleAdditionalImageUpload(files) {
      if (!files || files.length === 0) return

      // Si hay archivos nuevos seleccionados, agregarlos a los existentes
      const existingFiles = this.newAdditionalImages || []
      const newFiles = Array.from(files)

      // Verificar que no exceda el límite de 4 imágenes totales
      const totalSlots = 4
      const savedCount = this.savedAdditionalImages ? this.savedAdditionalImages.length - this.additionalImagesToRemove.length : 0
      const usedSlots = savedCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots

      // Si no hay slots disponibles, rechazar todo
      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} imágenes`)
        this.tempAdditionalImages = []
        return
      }

      // Si se seleccionaron más imágenes de las permitidas, tomar solo las que caben
      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} imagen(es). Se rechazaron ${rejected} imagen(es) por exceder el límite de ${totalSlots}.`
      }

      // Combinar archivos existentes con los nuevos (solo los que caben)
      const updatedFiles = [...existingFiles, ...filesToProcess]

      // Generar vistas previas para las nuevas imágenes - esperar a que todas se carguen
      const newPreviews = [...this.newAdditionalImagePreviews]
      const previewPromises = filesToProcess.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve(e.target.result)
          }
          reader.readAsDataURL(file)
        })
      })

      Promise.all(previewPromises).then(generatedPreviews => {
        newPreviews.push(...generatedPreviews)

        // Emitir todos los eventos después de que las vistas previas estén listas
        this.$emit('update:newAdditionalImagePreviews', newPreviews)
        this.$emit('update:newAdditionalImages', updatedFiles)

        // Mostrar mensaje apropiado
        const totalImages = savedCount + updatedFiles.length
        if (warningMessage) {
          this.$toast.warning(warningMessage)
        } else {
          this.$toast.success(`${filesToProcess.length} imagen(es) agregada(s). Total: ${totalImages}/${totalSlots}`)
        }
      }).catch(error => {
        console.error('Error al generar vistas previas:', error)
        this.$toast.error('Error al procesar las imágenes')
      })

      // Resetear el input temporal para permitir seleccionar más archivos
      this.$nextTick(() => {
        this.tempAdditionalImages = []
      })
    },
    openImageDialog(imageUrl) {
      this.selectedAdditionalImageUrl = imageUrl
      this.additionalImageDialog = true
    },
    removeSavedAdditionalDoc(index) {
      // Marcar para eliminar del servidor
      const doc = this.savedAdditionalDocs[index]
      const docId = doc && doc.id !== undefined ? doc.id : index
      const updatedDocsToRemove = [...this.additionalDocsToRemove, docId]
      this.$emit('update:additionalDocsToRemove', updatedDocsToRemove)
      this.$emit('additional-doc-removed', { type: 'saved', docId })
    },
    removeNewAdditionalDoc(index) {
      // Eliminar el archivo
      const updatedFiles = [...this.newAdditionalDocs]
      updatedFiles.splice(index, 1)
      this.$emit('update:newAdditionalDocs', updatedFiles)
      this.$emit('additional-doc-removed', { type: 'new', index })
    },
    handleAdditionalDocUpload(files) {
      if (!files || files.length === 0) return

      // Si hay archivos nuevos seleccionados, agregarlos a los existentes
      const existingFiles = this.newAdditionalDocs || []
      const newFiles = Array.from(files)

      // Verificar que no exceda el límite de 4 documentos totales
      const totalSlots = 4
      const savedCount = this.savedAdditionalDocs ? this.savedAdditionalDocs.length - this.additionalDocsToRemove.length : 0
      const usedSlots = savedCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots

      // Si no hay slots disponibles, rechazar todo
      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} documentos`)
        this.tempAdditionalDocs = []
        return
      }

      // Si se seleccionaron más documentos de los permitidos, tomar solo los que caben
      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} documento(s). Se rechazaron ${rejected} documento(s) por exceder el límite de ${totalSlots}.`
      }

      // Combinar archivos existentes con los nuevos (solo los que caben)
      const updatedFiles = [...existingFiles, ...filesToProcess]

      // Emitir eventos
      this.$emit('update:newAdditionalDocs', updatedFiles)

      // Mostrar mensaje apropiado
      const totalDocs = savedCount + updatedFiles.length
      if (warningMessage) {
        this.$toast.warning(warningMessage)
      } else {
        this.$toast.success(`${filesToProcess.length} documento(s) agregado(s). Total: ${totalDocs}/${totalSlots}`)
      }

      // Resetear el input temporal para permitir seleccionar más archivos
      this.$nextTick(() => {
        this.tempAdditionalDocs = []
      })
    },
    removeSavedDamageDoc(index) {
      // Marcar para eliminar del servidor
      const doc = this.savedDamageDocs[index]
      const docId = doc && doc.id !== undefined ? doc.id : index
      const updatedDocsToRemove = [...this.damageDocsToRemove, docId]
      this.$emit('update:damageDocsToRemove', updatedDocsToRemove)
      this.$emit('damage-doc-removed', { type: 'saved', docId })
    },
    undoRemoveSavedDamageDoc(index) {
      const doc = this.savedDamageDocs[index]
      const docId = doc && doc.id !== undefined ? doc.id : index
      const updatedDocsToRemove = this.damageDocsToRemove.filter(id => id !== docId)
      this.$emit('update:damageDocsToRemove', updatedDocsToRemove)
    },
    removeNewDamageDoc(index) {
      // Eliminar el archivo
      const updatedFiles = [...this.newDamageDocs]
      updatedFiles.splice(index, 1)
      this.$emit('update:newDamageDocs', updatedFiles)
      this.$emit('damage-doc-removed', { type: 'new', index })
    },
    handleDamageDocUpload(files) {
      if (!files || files.length === 0) return

      // Si hay archivos nuevos seleccionados, agregarlos a los existentes
      const existingFiles = this.newDamageDocs || []
      const newFiles = Array.from(files)

      // Verificar que no exceda el límite de 4 documentos totales
      const totalSlots = 4
      const savedCount = this.savedDamageDocs ? this.savedDamageDocs.length - this.damageDocsToRemove.length : 0
      const usedSlots = savedCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots

      // Si no hay slots disponibles, rechazar todo
      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} documentos de daños`)
        this.tempDamageDocs = []
        return
      }

      // Si se seleccionaron más documentos de los permitidos, tomar solo los que caben
      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} documento(s). Se rechazaron ${rejected} documento(s) por exceder el límite de ${totalSlots}.`
      }

      // Combinar archivos existentes con los nuevos (solo los que caben)
      const updatedFiles = [...existingFiles, ...filesToProcess]

      // Emitir eventos
      this.$emit('update:newDamageDocs', updatedFiles)

      // Mostrar mensaje apropiado
      const totalDocs = savedCount + updatedFiles.length
      if (warningMessage) {
        this.$toast.warning(warningMessage)
      } else {
        this.$toast.success(`${filesToProcess.length} documento(s) de daños agregado(s). Total: ${totalDocs}/${totalSlots}`)
      }

      // Resetear el input temporal para permitir seleccionar más archivos
      this.$nextTick(() => {
        this.tempDamageDocs = []
      })
    },
    downloadDamageDoc(doc) {
      // Implementar descarga del documento
      if (doc.url) {
        window.open(doc.url, '_blank')
      }
    },
    getFileIcon(filename) {
      if (!filename) return 'mdi-file-outline'
      const extension = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        txt: 'mdi-file-document-outline',
        zip: 'mdi-folder-zip',
        rar: 'mdi-folder-zip'
      }
      return iconMap[extension] || 'mdi-file-outline'
    },
    getFileIconColor(filename) {
      if (!filename) return 'grey'
      const extension = filename.split('.').pop().toLowerCase()
      const colorMap = {
        pdf: 'red',
        doc: 'blue',
        docx: 'blue',
        xls: 'green',
        xlsx: 'green',
        txt: 'grey',
        zip: 'orange',
        rar: 'orange'
      }
      return colorMap[extension] || 'grey'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 KB'
      const kb = bytes / 1024
      if (kb < 1024) {
        return `${kb.toFixed(2)} KB`
      }
      const mb = kb / 1024
      return `${mb.toFixed(2)} MB`
    },
    downloadDoc(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'documento'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.vehicle-info-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.vehicle-condition-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.vehicle-condition-group .v-input__slot {
  margin-bottom: 0;
}

.vehicle-condition-radio {
  position: absolute;
  right: 8px;
  bottom: 8px;
  margin: 0;
}

.vehicle-condition-radio .v-input--selection-controls__input {
  margin-right: 0;
}

.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.subtitle-1 {
  color: rgba(0, 0, 0, 0.87);
}

/* Additional Images Styles */
.additional-images-section {
  margin-top: 16px;
}

.image-preview-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.image-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-preview-card .v-card__actions {
  min-height: auto;
  padding: 4px 8px;
}

.damage-details-card {
  border-radius: 8px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.image-preview-card, .new-image-preview-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.image-preview {
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-card:hover .image-overlay,
.new-image-preview-card:hover .image-overlay {
  opacity: 1;
}

.image-preview-card:hover .image-preview,
.new-image-preview-card:hover .image-preview {
  transform: scale(1.05);
}

.image-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
}

.image-dialog-content {
  max-height: 80vh;
  background-color: #000;
}

.saved-images-container, .new-images-container {
  transition: all 0.3s ease;
}

.disabled-card {
  opacity: 0.5;
  cursor: default !important;
}

.error-border {
  border: 2px solid #ff5252 !important;
}

.saved-docs-container, .new-docs-container {
  transition: all 0.3s ease;
}

.disabled-item {
  opacity: 0.5;
}

.new-doc-item {
  background-color: rgba(76, 175, 80, 0.05);
  border-radius: 4px;
  margin-bottom: 4px;
}
</style>
